import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "../components/button";
import { H1 } from "../components/h1";
import { Modal } from "../components/modal";
import { useLocale } from "../stores/locale";

type ConfirmOptions = {
  header?: string;
  message?: React.ReactNode;
  type?: "yes-no";
  resolverBeforeClose?: () => Promise<void>;
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const t = useLocale((state) => state.locale);

  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [loading, setLoading] = useState(false);

  // store the resolver for the active promise
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((result: boolean) => {
    setOpen(false);
    setOpts(null);
    setLoading(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    setOpts(options);
    setOpen(true);
    setLoading(false);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  const type = opts?.type || "yes-no";
  const header = opts?.header || t.confirmation;

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {/* super basic modal UI - replace with your Dialog component if you have one */}
      {open && opts && (
        <Modal open size="xs" onClose={() => close(false)}>
          <form
            className="flex-1 flex flex-col items-center gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              (async () => {
                if (opts.resolverBeforeClose) {
                  setLoading(true);
                  await opts.resolverBeforeClose();
                  setLoading(false);
                }
                close(true);
              })();
            }}
          >
            <H1>{header}</H1>
            {opts.message && <p>{opts.message}</p>}
            <div className="flex-1 flex items-center gap-4">
              <Button
                loading={loading}
                severity={type === "yes-no" ? "primary" : "danger"}
                type="submit"
              >
                {type === "yes-no" ? t.yes : t.ok}
              </Button>
              <Button
                disabled={loading}
                severity="secondary"
                type="button"
                onClick={() => close(false)}
              >
                {type === "yes-no" ? t.no : t.cancel}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx.confirm;
}
